import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  applyThemeToDocument,
  readThemeCookie,
  resolveInitialTheme,
  THEME_BROADCAST_CHANNEL,
  THEME_STORAGE_KEY,
  writeThemeCookie,
  writeThemeStorage,
  type Theme,
} from './cookie'

export interface ThemeContextValue {
  theme: Theme
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
  isDark: boolean
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

function resolveDefaultTheme(defaultTheme?: Theme): Theme {
  return defaultTheme ?? resolveInitialTheme()
}

export function ThemeProvider({
  children,
  defaultTheme,
}: {
  children: ReactNode
  defaultTheme?: Theme
}) {
  const [theme, setThemeState] = useState<Theme>(() => resolveDefaultTheme(defaultTheme))

  const persistTheme = useCallback((nextTheme: Theme, broadcast = true) => {
    applyThemeToDocument(nextTheme)
    try {
      writeThemeStorage(nextTheme)
      writeThemeCookie(nextTheme)
    } catch {
      // localStorage/cookie peuvent être indisponibles (Safari private, policy navigateur).
    }

    if (broadcast && typeof BroadcastChannel !== 'undefined') {
      try {
        const channel = new BroadcastChannel(THEME_BROADCAST_CHANNEL)
        channel.postMessage(nextTheme)
        channel.close()
      } catch {
        // BroadcastChannel peut être bloqué par certains contextes navigateur.
      }
    }
  }, [])

  useEffect(() => {
    persistTheme(theme)
  }, [persistTheme, theme])

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY) return
      if (event.newValue !== 'light' && event.newValue !== 'dark') return
      setThemeState(event.newValue)
      applyThemeToDocument(event.newValue)
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined

    const channel = new BroadcastChannel(THEME_BROADCAST_CHANNEL)
    channel.onmessage = (event: MessageEvent<Theme>) => {
      if (event.data !== 'light' && event.data !== 'dark') return
      setThemeState(event.data)
      applyThemeToDocument(event.data)
    }

    return () => channel.close()
  }, [])

  useEffect(() => {
    const syncFromCookie = () => {
      const cookieTheme = readThemeCookie()
      if (!cookieTheme) return
      setThemeState((current) => {
        if (current === cookieTheme) return current
        applyThemeToDocument(cookieTheme)
        return cookieTheme
      })
    }

    window.addEventListener('focus', syncFromCookie)
    document.addEventListener('visibilitychange', syncFromCookie)

    return () => {
      window.removeEventListener('focus', syncFromCookie)
      document.removeEventListener('visibilitychange', syncFromCookie)
    }
  }, [])

  const setTheme = useCallback((nextTheme: Theme) => {
    setThemeState(nextTheme)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'))
  }, [])

  const value = useMemo(
    () => ({
      theme,
      toggleTheme,
      setTheme,
      isDark: theme === 'dark',
    }),
    [setTheme, theme, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
